/**
 * The change-history rail down the left of the game.
 *
 * Reads top-to-bottom oldest-to-newest, like a story: "Starter game", then each
 * thing they added. Tapping an entry loads that version — the game swaps and
 * starts playing, and that version's plan comes back on screen.
 *
 * The "will be replaced" styling on entries newer than the selected one is the
 * only warning a child gets before the confirm sheet, so it stays visible rather
 * than hiding them: seeing the versions that are about to go is what makes the
 * confirm make sense.
 */

import React from "react";
import { View, Text, Pressable, ScrollView, StyleSheet } from "react-native";
import type { CoderGameVersion } from "@amzn/a4k-creator-studio-shared";
import { colors, textStyle } from "../styles";
import { useCoderStore } from "./coderStore";

export default function VersionBar() {
  const versions = useCoderStore((s) => s.versions);
  const currentIndex = useCoderStore((s) => s.currentVersionIndex);
  const loadingIndex = useCoderStore((s) => s.loadingVersionIndex);
  const building = useCoderStore((s) => s.buildStatus === "building");

  return (
    <View style={styles.root}>
      <Text style={styles.header}>Your versions</Text>
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {versions.map((v) => (
          <VersionRow
            key={v.versionId}
            version={v}
            isCurrent={v.index === currentIndex}
            // Newer than the selected one: a build from here would delete it.
            willBeReplaced={v.index > currentIndex}
            isLoading={loadingIndex === v.index}
            disabled={building || loadingIndex !== null}
            onPress={() => useCoderStore.getState().loadVersion(v.index)}
          />
        ))}
        {building ? (
          // Placeholder for the version being built, so the rail visibly grows.
          <View style={[styles.row, styles.rowBuilding]}>
            <View style={styles.dotBuilding} />
            <Text style={styles.buildingText} numberOfLines={2}>
              building...
            </Text>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
}

function VersionRow({
  version,
  isCurrent,
  willBeReplaced,
  isLoading,
  disabled,
  onPress,
}: {
  version: CoderGameVersion;
  isCurrent: boolean;
  willBeReplaced: boolean;
  isLoading: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  const count = version.notes.length;
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        styles.row,
        isCurrent && styles.rowCurrent,
        willBeReplaced && styles.rowReplaced,
        disabled && !isCurrent && styles.rowDisabled,
      ]}
      testID={`coder-version-${version.index}`}
      accessibilityLabel={`Load version: ${version.name}`}
    >
      <View style={[styles.dot, isCurrent && styles.dotCurrent]} />
      <View style={styles.rowText}>
        <Text
          style={[styles.rowTitle, isCurrent && styles.rowTitleCurrent]}
          numberOfLines={2}
        >
          {version.name}
        </Text>
        {isLoading ? (
          <Text style={styles.rowMeta}>loading...</Text>
        ) : count > 0 ? (
          <Text style={styles.rowMeta}>
            {count} {count === 1 ? "idea" : "ideas"}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    width: 132,
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: "hidden",
  },
  header: {
    fontSize: 11,
    color: colors.textMuted,
    ...textStyle.heading,
    letterSpacing: 0.5,
    paddingHorizontal: 10,
    paddingTop: 9,
    paddingBottom: 6,
    textTransform: "uppercase",
  },
  list: { flex: 1 },
  listContent: { paddingBottom: 8, gap: 2 },

  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 9,
    paddingVertical: 7,
    marginHorizontal: 5,
    borderRadius: 8,
  },
  rowCurrent: { backgroundColor: colors.surfaceHover },
  // Dimmed, not hidden: the child should see what a build from here would cost.
  rowReplaced: { opacity: 0.4 },
  rowDisabled: { opacity: 0.3 },
  rowBuilding: { opacity: 0.7 },

  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.textMuted,
  },
  dotCurrent: { backgroundColor: colors.accent, width: 10, height: 10, borderRadius: 5 },
  dotBuilding: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.scene,
  },

  rowText: { flex: 1, gap: 1 },
  rowTitle: { fontSize: 12, color: colors.textSecondary, ...textStyle.body, lineHeight: 15 },
  rowTitleCurrent: { color: colors.text, ...textStyle.heading },
  rowMeta: { fontSize: 10, color: colors.textMuted, ...textStyle.body },
  buildingText: { flex: 1, fontSize: 12, color: colors.scene, ...textStyle.body },
});
